import { ExpressionPart, ExpressionPartTypes } from 'src/models/calculations/CalculationDetailsResult';
import { AllocationGroupFormula } from 'src/models/tp-allocation/AllocationGroup';

const flatten = (parts: ExpressionPart[]): ExpressionPart[] =>
    parts.reduce((all, part) => all.concat(part, part.subTokens ? flatten(part.subTokens) : []), [] as ExpressionPart[]);

const nameOf = (formula: AllocationGroupFormula) => (formula.description || '').trim();

const codesOf = (formula: AllocationGroupFormula) =>
    (formula.mapping?.values ?? []).map((v) => v.trim()).filter((v) => v !== '');

/**
 * Checks run before a Step 3 formula is sent; the backend runs the same checks. A formula needs a name unique
 * in its group (trimmed, any case), a formula whose references are Step 1 descriptions or Step 2 totals, and
 * company codes that are known and not mapped by another formula of the group. Returns the first problem, or
 * undefined.
 * @param formula The formula being saved, with the tokens the save will send.
 * @param groupFormulas Every formula of its group, the formula itself included.
 * @param companies Known company codes; when empty the codes are left to the backend.
 */
export function validateDasFormula(formula: AllocationGroupFormula, groupFormulas: AllocationGroupFormula[],
                                   companies: string[]): string | undefined {
    const name = nameOf(formula);
    if (!name) {
        return `Formula #${formula.visibleSequence ?? formula.sequence} needs a name.`;
    }
    const others = groupFormulas.filter((f) => f.allocationFormulaId !== formula.allocationFormulaId);
    if (others.some((f) => nameOf(f).toLowerCase() === name.toLowerCase())) {
        return 'Formula names must be unique in a group.';
    }
    if (!(formula.expression || '').trim()) {
        return `"${name}" needs a formula.`;
    }
    const unknownRefs = flatten(formula.expressionTokens ?? [])
        .filter((p) => p.type === ExpressionPartTypes.PARAMETER || p.type === ExpressionPartTypes.UNKNOWN);
    if (unknownRefs.length > 0) {
        return `"${name}" references unknown item(s): ${unknownRefs.map((p) => p.value || '[]').join(', ')}.`;
    }
    const codes = codesOf(formula);
    const unknownCodes = companies.length > 0 ? codes.filter((c) => !companies.includes(c)) : [];
    if (unknownCodes.length > 0) {
        return `"${name}" maps unknown company code(s): ${unknownCodes.join(', ')}.`;
    }
    for (const other of others) {
        const taken = codes.filter((c) => codesOf(other).includes(c));
        if (taken.length > 0) {
            return `Company code(s) ${taken.join(', ')} are already mapped by formula "${nameOf(other)}".`;
        }
    }
    return undefined;
}
